import styled from '@emotion/styled';
import {
  Box,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Paper as MuiPaper,
  Typography,
} from '@mui/material';
import { useContext, useMemo, useState } from 'react';

import { GridRowSelectionModel } from '@mui/x-data-grid';
import { concat, unionBy } from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import CustomCheckbox from '../../components/CustomTableCheckbox';
import { JobBenchmarkingTable } from '../../components/table/JobBenchmarkingTable';
import { TablePlaceholder } from '../../components/TablePlaceholder';
import { JobBenchmarkingContext } from '../../contexts/JobBenchmarkingContext';
import { IJobBenchmarking } from '../../interfaces/Job';
import { useLocale } from '../../providers/LocaleProvider';
import ChecklistIcons from '../../resources/images/icons-checklist.svg';
import { KLAYO_COLORS } from '../../themes';
import { JobBenchMarkingType } from '../../utilities/validationSchema';

export type TasksResultTabProps = {
  theme: any;
  results?: IJobBenchmarking;
};

const TasksResultTab = ({ theme, results }: TasksResultTabProps) => {
  const [actionTarget, setActionTarget] = useState<null | HTMLElement>(null);
  const [currentRow, setCurrentRow] = useState<any>(null);

  const { setValue, watch } = useFormContext<JobBenchMarkingType>();
  const { setShowMatched, setShowUnmatched, showMatched, showUnmatched } =
    useContext(JobBenchmarkingContext);
  const { translate } = useLocale();

  const renamedTasks = watch('renamedTasks') || [];
  const addedStandardMatchedTasks = watch('addedStandardMatchedTasks') || [];
  const addedStandardUnmatchedTasks = watch('addedStandardUnmatchedTasks') || [];
  const addedStandardUnmatchedCompetencies = watch('addedStandardUnmatchedCompetencies') || [];
  const addedStandardMatchedTaskCompetencies = watch('addedStandardMatchedTaskCompetencies') || [];

  const matchedYourTasks = useMemo(() => {
    // Drop locked teaser rows: this legacy tab has no blur/disable UI, so over-limit
    // isExtra rows would render as normal selectable rows. The paywall is conveyed by
    // the separate "contact to upgrade" panel (hasLimitedAccess).
    const matchedTasks = concat(
      results?.taskComparison.exactMatches || [],
      results?.taskComparison.fuzzyMatches || []
    ).filter((match) => !match.standardLibraryTask?.isExtra);

    return (
      matchedTasks.map((match) => {
        const yourJobId = match.organizationTask.id;
        const industryStandardJobId = match.standardLibraryTask.id;

        const renamed = renamedTasks.find((t) => t.id === yourJobId);
        const added = addedStandardMatchedTasks.find((t) => t.id === industryStandardJobId);

        return {
          id: `${yourJobId}_${industryStandardJobId}`,
          yourJob: match.organizationTask.name,
          industryStandardJob: match.standardLibraryTask.name,
          yourJobId: yourJobId,
          industryStandardJobId: industryStandardJobId,
          isRenamed: !!renamed,
          isAdded: !!added,
          standardTaskCompetencies: match.standardLibraryTask.taskCompetencies?.map((c) => ({
            id: c.id,
            name: c.name,
          })),
        };
      }) || []
    );
  }, [results, renamedTasks, addedStandardMatchedTasks]);

  const ignoredMatchedTasks = useMemo(() => {
    return (
      matchedYourTasks
        .filter((match) => {
          return addedStandardMatchedTasks.some((t) => t.id === match.industryStandardJobId);
        })
        .map((t) => ({
          id: t.yourJobId,
          name: t.yourJob,
        })) || []
    );
  }, [matchedYourTasks, addedStandardMatchedTasks]);

  const organizationTasks = useMemo(() => {
    return concat(
      results?.taskComparison.onlyInOrganization.map((task) => ({
        id: task.id,
        name: task.name,
      })) || [],
      ignoredMatchedTasks
    );
  }, [results, ignoredMatchedTasks]);

  const industryStandardTasks = useMemo(() => {
    return (
      results?.taskComparison.onlyInStandardLibrary
        .filter((task) => !task.isExtra)
        .map((task) => ({
          id: task.id,
          name: task.name,
          taskCompetencies: task.taskCompetencies?.map((c) => ({
            id: c.id,
            name: c.name,
          })),
        })) || []
    );
  }, [results]);

  const matchedTasksCount = useMemo(() => {
    return matchedYourTasks.length || 0;
  }, [matchedYourTasks]);

  const unmatchedYourTasksCount = useMemo(() => {
    return organizationTasks.length || 0;
  }, [organizationTasks]);

  const unmatchedIndustryStandardTasksCount = useMemo(() => {
    return industryStandardTasks.length || 0;
  }, [industryStandardTasks]);

  const unmatchedTasksCount = useMemo(() => {
    return unmatchedYourTasksCount + unmatchedIndustryStandardTasksCount;
  }, [unmatchedYourTasksCount, unmatchedIndustryStandardTasksCount]);

  const isMultipleMatchedTasks = useMemo(() => matchedTasksCount > 1, [matchedTasksCount]);
  const isMultipleUnmatchedTasks = useMemo(() => unmatchedTasksCount > 1, [unmatchedTasksCount]);
  const isMultipleUnmatchedYourTasks = useMemo(
    () => unmatchedYourTasksCount > 1,
    [unmatchedYourTasksCount]
  );
  const isMultipleUnmatchedIndustryStandardTasks = useMemo(
    () => unmatchedIndustryStandardTasksCount > 1,
    [unmatchedIndustryStandardTasksCount]
  );

  const hasLimitedAccess = useMemo(
    () => results?.taskComparison.hasLimitedAccess || false,
    [results?.taskComparison.hasLimitedAccess]
  );

  const handleActionMenu = (event: any, row: any) => {
    setActionTarget(event.currentTarget);
    setCurrentRow(row);
  };

  const onCloseActionMenu = () => {
    setActionTarget(null);
    setCurrentRow(null);
  };

  const onToggleRenameTask = () => {
    if (!currentRow) return;

    let updatedRenamedTasks;
    if (currentRow.isRenamed) {
      // Revert: remove from renamedTasks
      updatedRenamedTasks = renamedTasks.filter((t) => t.id !== currentRow.yourJobId);
    } else {
      // Rename: add to renamedTasks with industry standard name
      updatedRenamedTasks = [
        ...renamedTasks,
        { id: currentRow.yourJobId, name: currentRow.industryStandardJob },
      ];
    }
    setValue('renamedTasks', updatedRenamedTasks, { shouldDirty: true });
    onCloseActionMenu();
  };

  const onTakeNewTask = () => {
    if (!currentRow) return;

    if (currentRow.isAdded) {
      // Revert: remove from addedStandardMatchedTasks
      const updatedAddedTasks = addedStandardMatchedTasks.filter(
        (task) => task.id !== currentRow.industryStandardJobId
      );
      setValue('addedStandardMatchedTasks', updatedAddedTasks, { shouldDirty: true });
      // Optionally: remove related competencies from addedStandardMatchedTaskCompetencies here

      if (Array.isArray(currentRow.standardTaskCompetencies)) {
        const competencyIdsToRemove = currentRow.standardTaskCompetencies.map(
          (c: { id: string }) => c.id
        );
        const updatedCompetencies = addedStandardMatchedTaskCompetencies.filter(
          (ac: { id: string }) => !competencyIdsToRemove.includes(ac.id)
        );
        setValue('addedStandardMatchedTaskCompetencies', updatedCompetencies, {
          shouldDirty: true,
        });
      }
    } else {
      // Add the industry standard task as a new task if not already present
      if (!addedStandardMatchedTasks.some((task) => task.id === currentRow.industryStandardJobId)) {
        const newTask = {
          id: currentRow.industryStandardJobId,
          name: currentRow.industryStandardJob,
        };
        setValue('addedStandardMatchedTasks', [...addedStandardMatchedTasks, newTask], {
          shouldDirty: true,
        });

        // Add competencies from currentRow.standardTaskCompetencies
        if (Array.isArray(currentRow.standardTaskCompetencies)) {
          const newCompetencies = currentRow.standardTaskCompetencies
            .filter(
              (c: { id: string; name: string }) =>
                !addedStandardMatchedTaskCompetencies.some((ac: { id: string }) => ac.id === c.id)
            )
            .map((c: { id: string; name: string }) => ({ id: c.id, name: c.name }));
          if (newCompetencies.length > 0) {
            setValue(
              'addedStandardMatchedTaskCompetencies',
              [...addedStandardMatchedTaskCompetencies, ...newCompetencies],
              { shouldDirty: true }
            );
          }
        }
      }
    }
    onCloseActionMenu();
  };

  const onAddUnmatchedTasks = (newSelection: GridRowSelectionModel) => {
    const selectedTasks = industryStandardTasks.filter((task) => newSelection.includes(task.id));

    // Get competencies from newly selected tasks
    const newTaskCompetencies = newSelection.flatMap((taskId) => {
      const task = industryStandardTasks.find((t) => t.id === taskId);
      return task?.taskCompetencies || [];
    });

    // Get competencies from previously selected tasks
    const oldTaskCompetencies = addedStandardUnmatchedTasks.flatMap((unmatchedTask) => {
      const task = industryStandardTasks.find((t) => t.id === unmatchedTask.id);
      return task?.taskCompetencies || [];
    });

    // Get competencies that were removed (in old but not in new)
    const removedCompetencies = oldTaskCompetencies.filter(
      (id) => !newTaskCompetencies.includes(id)
    );

    // Update selected competencies
    const updatedUnmatchedCompetencies = addedStandardUnmatchedCompetencies.filter(
      (competency) => !removedCompetencies.some((c) => c.id === competency.id)
    );

    const finalAddedStandardUnmatchedCompetencies = unionBy(
      [...updatedUnmatchedCompetencies, ...newTaskCompetencies],
      'id'
    );

    setValue('addedStandardUnmatchedTasks', selectedTasks, {
      shouldDirty: true,
    });

    setValue('addedStandardUnmatchedCompetencies', finalAddedStandardUnmatchedCompetencies, {
      shouldDirty: true,
    });
  };

  return (
    <Box display={'flex'} flexDirection={'column'} gap={3}>
      {!!matchedTasksCount && (
        <Paper>
          <Box p={3} display={'flex'} flexDirection={'column'} gap={2}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography fontWeight={600} variant='body1'>
                {translate(
                  isMultipleMatchedTasks
                    ? 'settingsViews.jobBenchmarking.matchedTasks_plural'
                    : 'settingsViews.jobBenchmarking.matchedTasks',
                  { count: matchedTasksCount }
                )}
              </Typography>

              <PButton
                onClick={() =>
                  setShowMatched({
                    ...showMatched,
                    task: !showMatched.task,
                  })
                }
              >
                {showMatched.task
                  ? translate('settingsViews.jobBenchmarking.hideDetails')
                  : translate('settingsViews.jobBenchmarking.showDetails')}
              </PButton>
            </Box>

            {!showMatched.task ? (
              <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                {translate(
                  isMultipleMatchedTasks
                    ? 'settingsViews.jobBenchmarking.weFoundMatchedTasks_plural'
                    : 'settingsViews.jobBenchmarking.weFoundMatchedTasks',
                  { count: matchedTasksCount }
                )}
              </Typography>
            ) : (
              <JobBenchmarkingTable
                columns={[
                  {
                    type: JobBenchmarkingTable.columns.yourJob,
                    sortable: false,
                    label: translate('settingsViews.jobBenchmarking.yourJob').toUpperCase(),
                  },
                  {
                    type: JobBenchmarkingTable.columns.industryStandardJob,
                    sortable: false,
                    cellClassName: 'pl-4',
                    headerClassName: 'pl-4',
                    label: translate(
                      'settingsViews.jobBenchmarking.klayoIntelligenceJob'
                    ).toUpperCase(),
                  },
                  {
                    type: JobBenchmarkingTable.columns.actions,
                    label: '',
                  },
                ]}
                rows={matchedYourTasks}
                pagination={false}
                paper={false}
                theme={theme}
                selectable={false}
                dense={true}
                showFooter={false}
                onActionMenu={handleActionMenu}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: 'none', // remove border-bottom in header
                  },
                }}
              />
            )}
          </Box>
        </Paper>
      )}
      {!!unmatchedTasksCount && (
        <Paper>
          <Box p={3} display={'flex'} flexDirection={'column'} gap={2}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography fontWeight={600} variant='body1'>
                {translate(
                  isMultipleUnmatchedTasks
                    ? 'settingsViews.jobBenchmarking.unmatchedTasks_plural'
                    : 'settingsViews.jobBenchmarking.unmatchedTasks',
                  { count: unmatchedTasksCount }
                )}
              </Typography>

              <PButton
                onClick={() =>
                  setShowUnmatched({
                    ...showUnmatched,
                    task: !showUnmatched.task,
                  })
                }
              >
                {showUnmatched.task
                  ? translate('settingsViews.jobBenchmarking.hideDetails')
                  : translate('settingsViews.jobBenchmarking.showDetails')}
              </PButton>
            </Box>

            {!showUnmatched.task ? (
              <Typography color={KLAYO_COLORS.GreyGR500} variant='body2'>
                {translate('settingsViews.jobBenchmarking.weFoundUnmatchedTasks', {
                  yourCount: unmatchedYourTasksCount,
                  standardCount: unmatchedIndustryStandardTasksCount,
                })}
              </Typography>
            ) : (
              <Grid container>
                <Grid item xs={6}>
                  <JobBenchmarkingTable
                    columns={[
                      {
                        type: JobBenchmarkingTable.columns.name,
                        sortable: false,
                        label: translate('settingsViews.jobBenchmarking.yourJob').toUpperCase(),
                      },
                    ]}
                    rows={organizationTasks}
                    pagination={false}
                    paper={false}
                    theme={theme}
                    selectable={false}
                    dense={true}
                    showFooter={false}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none', // remove border-bottom in header
                      },
                    }}
                    noResultsComponent={
                      <TablePlaceholder
                        text={translate('settingsViews.jobBenchmarking.noMoreTasksInYourJob')}
                        icon={<ChecklistIcons />}
                      />
                    }
                    noRowsComponent={
                      <TablePlaceholder
                        text={translate('settingsViews.jobBenchmarking.noMoreTasksInYourJob')}
                        icon={<ChecklistIcons />}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <JobBenchmarkingTable
                    columns={[
                      {
                        type: JobBenchmarkingTable.columns.name,
                        sortable: false,
                        label: translate(
                          'settingsViews.jobBenchmarking.klayoIntelligenceJob'
                        ).toUpperCase(),
                      },
                      {
                        type: JobBenchmarkingTable.columns.checkbox,
                      },
                    ]}
                    rows={industryStandardTasks}
                    pagination={false}
                    paper={false}
                    theme={theme}
                    selectable={false}
                    dense={true}
                    showFooter={false}
                    onActionMenu={handleActionMenu}
                    checkboxSelection
                    slots={{
                      baseCheckbox: CustomCheckbox as React.ComponentType<unknown>,
                    }}
                    rowSelectionModel={addedStandardUnmatchedTasks.map((task) => task.id)}
                    onRowSelectionModelChange={onAddUnmatchedTasks}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none', // remove border-bottom in header
                      },
                      '& .MuiDataGrid-columnHeaderCheckbox': {
                        display: 'none !important', // Visually hide header checkbox
                      },
                    }}
                    noResultsComponent={
                      <TablePlaceholder
                        text={translate('settingsViews.jobBenchmarking.noMoreTasksInStandardJob')}
                        icon={<ChecklistIcons />}
                      />
                    }
                    noRowsComponent={
                      <TablePlaceholder
                        text={translate('settingsViews.jobBenchmarking.noMoreTasksInStandardJob')}
                        icon={<ChecklistIcons />}
                      />
                    }
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>
      )}
      {hasLimitedAccess && (
        <Paper>
          <Box p={3} textAlign={'center'}>
            {translate('settingsViews.jobBenchmarking.contactToUpgrade')}
          </Box>
        </Paper>
      )}
      <Menu
        open={actionTarget !== null}
        anchorEl={actionTarget}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={onCloseActionMenu}
      >
        <MenuItem disabled={currentRow?.isAdded} onClick={onToggleRenameTask}>
          <ListItemText>
            {currentRow?.isRenamed
              ? translate('settingsViews.jobBenchmarking.revertTaskName')
              : translate('settingsViews.jobBenchmarking.renameTaskName')}
          </ListItemText>
        </MenuItem>
        <MenuItem disabled={currentRow?.isRenamed} onClick={onTakeNewTask}>
          <ListItemText>
            {currentRow?.isAdded
              ? translate('settingsViews.jobBenchmarking.dontTakeAsNewTask')
              : translate('settingsViews.jobBenchmarking.takeAsNewTask')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TasksResultTab;

const PButton = styled.p`
  color: #4d5ef7;
  cursor: pointer;
  font-weight: 600;
  margin: 0;
  z-index: 10;
`;

const Paper = styled(MuiPaper)`
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border-radius: 0.875rem;
  border: 2px solid rgba(230, 230, 230, 0.83);
`;
